import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useAuth } from '../hooks/useAuth';
import { takeSignupExtras, clearSignupExtras } from '../utils/trialSignup';

// The popup is only downloaded when someone opens it.
const TrialSignupModal = lazy(() => import('../components/TrialSignup/TrialSignupModal'));

const TrialSignupContext = createContext(null);

/**
 * Opens the "Start 14-day trial" popup from any button (TrialButton), and
 * saves the company and phone a Google sign-up left waiting.
 */
export function TrialSignupProvider({ children }) {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(null); // null, or { plan, interval }

  const openTrial = useCallback((opts = {}) => setOpen({ plan: opts.plan, interval: opts.interval }), []);
  const closeTrial = useCallback(() => setOpen(null), []);

  // Google sign-up from the popup: company and phone come back with us in
  // localStorage, and go on the profile once the account exists.
  const userId = currentUser?.id;
  useEffect(() => {
    if (!userId) return;
    const extras = takeSignupExtras();
    if (!extras) return;
    clearSignupExtras();
    // Loaded here, not at the top: every page with a trial button imports
    // this module, and most of them never need the API client for it.
    import('../utils/api')
      .then(({ authAPI }) => authAPI.updateProfile({ phone: extras.phone, companyName: extras.company }))
      .catch(() => {});
  }, [userId]);

  const value = useMemo(() => ({ openTrial, closeTrial }), [openTrial, closeTrial]);

  return (
    <TrialSignupContext.Provider value={value}>
      {children}
      {open && (
        <Suspense fallback={null}>
          <TrialSignupModal initialPlan={open.plan} initialInterval={open.interval} onClose={closeTrial} />
        </Suspense>
      )}
    </TrialSignupContext.Provider>
  );
}

/** { openTrial, closeTrial }, or null outside the provider. */
export function useTrialSignup() {
  return useContext(TrialSignupContext);
}

export default TrialSignupContext;
